/**
 * Intégrateur — velocity Verlet.
 *
 * Verlet-0 n'en avait pas, et c'était délibéré : Ératosthène est de la
 * géométrie, rien n'y est intégré dans le temps, et un noyau écrit « au cas
 * où » aurait été du code validé mais mort. Il arrive avec la première
 * expérience qui en a besoin, contre les bancs analytiques que SPEC §9 nomme,
 * écrits rouges avant lui.
 *
 * ## Pourquoi velocity Verlet, et pourquoi RK4 est interdit
 *
 * RK4 est plus précis à pas égal — sur un pas. Ce n'est pas le critère. Sur
 * une orbite parcourue mille fois, ce qui compte est de savoir si l'erreur
 * d'énergie **oscille** autour de zéro ou **dérive** dans une direction.
 *
 * Velocity Verlet est symplectique : il préserve exactement la structure
 * géométrique du flot hamiltonien, donc l'énergie reste bornée indéfiniment.
 * RK4 ne l'est pas : son erreur d'énergie s'accumule de façon monotone, et
 * l'orbite spirale — lentement, sûrement, et de manière invisible sur les cent
 * premiers pas. C'est un bug qui ne se voit qu'après le moment où on a cessé
 * de regarder, et c'est pour ça que l'interdit est écrit dans CLAUDE.md §5
 * plutôt que laissé au jugement.
 *
 * Le banc d'énergie ne mesure donc pas la petitesse de l'erreur mais l'absence
 * de tendance.
 *
 * ## Une propriété qu'on n'attendait pas d'un intégrateur
 *
 * Sous accélération **constante**, velocity Verlet est exact : il reproduit
 * `x = x₀ + v₀t + ½at²` à la précision de la machine, quel que soit le pas.
 * Le banc de chute libre l'exige à 1e-9 avec un seul pas de dix secondes —
 * ce n'est pas une tolérance généreuse, c'est la propriété.
 *
 * Deux dimensions : ce qui suffit à un plan orbital et à un tir balistique.
 * La troisième arrivera avec l'expérience qui la réclame.
 */

import { coreFr } from "../i18n/core-fr.js";
import type { Quantity } from "./units.js";
import {
  UnitError,
  add,
  dimensionless,
  displacement,
  scale,
  valueIn,
  velocityChange,
} from "./units.js";

export interface Vector2<D extends "length" | "velocity" | "acceleration"> {
  readonly x: Quantity<D>;
  readonly y: Quantity<D>;
}

export interface Body {
  readonly position: Vector2<"length">;
  readonly velocity: Vector2<"velocity">;
  readonly mass: Quantity<"mass">;
}

/**
 * Le champ d'accélération subi en un point.
 *
 * Une **accélération**, pas une force : c'est ce dont l'intégrateur a besoin,
 * et le passage par la masse appartient au modèle qui définit le champ. Un
 * champ de gravitation ne dépend pas de la masse du corps qui le subit — c'est
 * précisément ce que dit le principe d'équivalence, et le type le rappelle.
 */
export type Field = (position: Vector2<"length">) => Vector2<"acceleration">;

const HALF = dimensionless(0.5);

function assertUsableStep(duration: Quantity<"time">): void {
  const seconds = valueIn(duration, "s");
  if (seconds <= 0) {
    throw new UnitError(
      coreFr.integrator.invalidStep(String(seconds)),
    );
  }
}

/**
 * Un pas de velocity Verlet.
 *
 * ```
 * x₁ = x₀ + v₀·dt + ½·a₀·dt²
 * v₁ = v₀ + ½·(a₀ + a₁)·dt
 * ```
 *
 * L'accélération est évaluée deux fois par pas — une fois au départ, une fois
 * à l'arrivée — et c'est cette moyenne des deux qui rend le schéma
 * symplectique. La remplacer par la seule `a₀` donnerait Euler semi-implicite,
 * moins précis ; par `a₁` seule, un schéma instable.
 *
 * Ne modifie rien : les corps sont immuables, et deux simulations qui
 * partagent un corps ne peuvent pas se marcher dessus.
 */
export function step(body: Body, field: Field, duration: Quantity<"time">): Body {
  assertUsableStep(duration);

  const a0 = field(body.position);

  // x₁ = x₀ + v₀·dt + ½·a₀·dt²
  const halfStepVelocity: Vector2<"velocity"> = {
    x: add(body.velocity.x, scale(velocityChange(a0.x, duration), HALF)),
    y: add(body.velocity.y, scale(velocityChange(a0.y, duration), HALF)),
  };
  const position: Vector2<"length"> = {
    x: add(body.position.x, displacement(halfStepVelocity.x, duration)),
    y: add(body.position.y, displacement(halfStepVelocity.y, duration)),
  };

  // v₁ = v½ + ½·a₁·dt
  const a1 = field(position);
  const velocity: Vector2<"velocity"> = {
    x: add(halfStepVelocity.x, scale(velocityChange(a1.x, duration), HALF)),
    y: add(halfStepVelocity.y, scale(velocityChange(a1.y, duration), HALF)),
  };

  return { position, velocity, mass: body.mass };
}

/**
 * `count` pas successifs.
 *
 * La boucle est écrite ici plutôt que laissée à l'appelant pour une seule
 * raison : elle est le seul endroit où l'ordre des pas compte, et le garder
 * sous la main évite qu'une itération sur une collection non ordonnée s'y
 * glisse un jour (CLAUDE.md §5).
 */
export function integrate(
  body: Body,
  field: Field,
  duration: Quantity<"time">,
  count: Quantity<"dimensionless">,
): Body {
  const steps = valueIn(count, "1");
  if (!Number.isInteger(steps) || steps < 0) {
    throw new UnitError(coreFr.integrator.invalidStepCount(String(steps)));
  }

  let current = body;
  for (let index = 0; index < steps; index += 1) {
    current = step(current, field, duration);
  }
  return current;
}
